import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, retry } from "rxjs";
import {
  CourseSuggestion,
  MyProfile,
  MyRecommendation,
  Quiz,
  QuizAttempt,
  SkillNarrative,
} from "../model/quiz.model";

/**
 * The quiz engine — the replacement for the legacy exam system.
 *
 * The difference that matters: a quiz is a BLUEPRINT (how many questions, from
 * which topics, at which difficulty), not a fixed paper. Every attempt is dealt
 * from the shared question bank, so two students sitting the same quiz do not
 * get the same questions, and a retake is a new draw.
 *
 * The API decides what a student is allowed to see. Mid-quiz payloads carry no
 * answer key at all; it appears only once the attempt is submitted.
 */
@Injectable({
  providedIn: "root",
})
export class QuizService {
  private apiQuizUrl = "/quizzes";
  private apiAttemptUrl = "/quiz_attempts";
  private apiSkillUrl = "/skill_reports";

  #http = inject(HttpClient);

  _setFilter(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v != null),
    );
  }

  _setParams(value: any) {
    if (!value) return;
    const filter: any = this._setFilter(value);
    let params = new HttpParams();
    for (const key in filter) {
      if (Array.isArray(filter[key])) {
        params = params.append(key, JSON.stringify(filter[key]));
      } else {
        params = params.append(key, filter[key]);
      }
    }
    return params;
  }

  /** Quizzes the student may sit. The API only returns quizzes on courses they
   *  are actually enrolled in, so no client-side filtering is needed. */
  getQuizzes(filter?: any) {
    const params = this._setParams({ datatable: true, limit: 100, ...filter }) || "";
    return this.#http.get<any>(`${this.apiQuizUrl}?${params}`).pipe(retry(1));
  }

  getQuiz(id: number): Observable<Quiz> {
    return this.#http.get<Quiz>(`${this.apiQuizUrl}/${id}`).pipe(retry(1));
  }

  /**
   * Start or resume.
   *
   * The API hands back the OPEN attempt if one exists rather than dealing a new
   * paper, so refreshing mid-quiz keeps the same questions and the same answers
   * already given.
   */
  startAttempt(quizId: number): Observable<QuizAttempt> {
    return this.#http.post<QuizAttempt>(`${this.apiAttemptUrl}/start`, {
      quiz_id: quizId,
    });
  }

  getAttempt(id: number): Observable<QuizAttempt> {
    return this.#http.get<QuizAttempt>(`${this.apiAttemptUrl}/${id}`).pipe(retry(1));
  }

  /** Autosave mid-paper. Fire-and-forget: losing one of these costs the student
   *  nothing, the next keystroke sends the whole map again. */
  saveDraftAnswers(
    id: number,
    answers: { quiz_question_id: number; answer: string | null }[],
  ) {
    return this.#http.put(`${this.apiAttemptUrl}/save_answers/${id}`, { answers });
  }

  submitAttempt(
    id: number,
    answers: { quiz_question_id: number; answer: string | null }[],
  ): Observable<QuizAttempt> {
    return this.#http.put<QuizAttempt>(`${this.apiAttemptUrl}/submit/${id}`, {
      answers,
    });
  }

  getAttempts(filter?: any) {
    const params = this._setParams({ datatable: true, limit: 20, ...filter }) || "";
    return this.#http.get<any>(`${this.apiAttemptUrl}?${params}`).pipe(retry(1));
  }

  // ---- the student's own skill picture -------------------------------------
  // Same endpoints the teacher's report uses; the API scopes them to the caller
  // when no student id is given.

  getMyProfile(): Observable<MyProfile> {
    return this.#http.get<MyProfile>(`${this.apiSkillUrl}/student`).pipe(retry(1));
  }

  getMyRecommendations(limit = 5): Observable<MyRecommendation[]> {
    return this.#http
      .get<MyRecommendation[]>(`${this.apiSkillUrl}/recommendations?limit=${limit}`)
      .pipe(retry(1));
  }

  /**
   * The same three reports, optionally for a named student.
   *
   * A parent has to name the child; the API refuses to guess, because a parent
   * of two who omits it would otherwise be shown the wrong child's report.
   */
  getProfile(studentId?: number): Observable<MyProfile> {
    return this.#http
      .get<MyProfile>(`${this.apiSkillUrl}/student${studentId ? "/" + studentId : ""}`)
      .pipe(retry(1));
  }

  getRecommendations(studentId?: number, limit = 5): Observable<MyRecommendation[]> {
    return this.#http
      .get<MyRecommendation[]>(
        `${this.apiSkillUrl}/recommendations${studentId ? "/" + studentId : ""}?limit=${limit}`,
      )
      .pipe(retry(1));
  }

  getNarrative(studentId?: number): Observable<SkillNarrative> {
    return this.#http
      .get<SkillNarrative>(`${this.apiSkillUrl}/narrative${studentId ? "/" + studentId : ""}`)
      .pipe(retry(1));
  }

  getCourseSuggestions(studentId?: number, limit = 3): Observable<CourseSuggestion[]> {
    return this.#http
      .get<CourseSuggestion[]>(
        `${this.apiSkillUrl}/course_suggestions${studentId ? "/" + studentId : ""}?limit=${limit}`,
      )
      .pipe(retry(1));
  }

  /** Build a drill for one weak topic. The API reuses an unfinished practice
   *  set rather than creating a new one on every press. */
  createPractice(subLessonId: number, count = 5): Observable<Quiz> {
    return this.#http.post<Quiz>(`${this.apiQuizUrl}/practice`, {
      sub_lesson_ids: [subLessonId],
      count,
    });
  }
}
